import type { RadarConfig } from "@/features/client-config/types/client-config";
import { normalizeHttpUrl, normalizeWsUrl } from "@/utils/url";

type CloudRadar = {
  id: string;
  url: string | null;
  name: string;
  serialNumber: string;
};

type RebindRadarIdsResult = {
  radars: RadarConfig[];
  rebound: number;
  defaultRadarId?: string;
};

const matchesCloudRadar = (
  radar: RadarConfig,
  cloudRadar: CloudRadar
): boolean => {
  if (cloudRadar.url === null) {
    return false;
  }
  return (
    normalizeHttpUrl(cloudRadar.url) === radar.radarHttpBaseUrl ||
    normalizeWsUrl(cloudRadar.url) === radar.webSocketBaseUrl
  );
};

export const rebindRadarIds = (
  localRadars: RadarConfig[],
  cloudRadars: CloudRadar[],
  defaultRadarId?: string
): RebindRadarIdsResult => {
  const cloudIds = new Set(cloudRadars.map((cloudRadar) => cloudRadar.id));
  const idsToRebind = new Map<string, RadarConfig>();

  for (const radar of localRadars) {
    if (cloudIds.has(radar.id)) {
      continue;
    }
    const matches = cloudRadars.filter((cloudRadar) =>
      matchesCloudRadar(radar, cloudRadar)
    );
    if (matches.length === 1) {
      idsToRebind.set(radar.id, radar);
    }
  }

  const matchCounts = new Map<string, number>();
  for (const radar of idsToRebind.values()) {
    const match = cloudRadars.find((cloudRadar) =>
      matchesCloudRadar(radar, cloudRadar)
    );
    if (!match) {
      continue;
    }
    matchCounts.set(match.id, (matchCounts.get(match.id) ?? 0) + 1);
  }

  const oldToNewId = new Map<string, string>();
  const radars = localRadars.map((radar) => {
    if (!idsToRebind.has(radar.id)) {
      return radar;
    }
    const match = cloudRadars.find((cloudRadar) =>
      matchesCloudRadar(radar, cloudRadar)
    );
    if (!match || (matchCounts.get(match.id) ?? 0) > 1) {
      return radar;
    }
    oldToNewId.set(radar.id, match.id);
    return {
      ...radar,
      id: match.id,
      name: match.name,
      serialNumber: match.serialNumber,
    };
  });

  return {
    radars,
    rebound: oldToNewId.size,
    defaultRadarId:
      defaultRadarId !== undefined
        ? (oldToNewId.get(defaultRadarId) ?? defaultRadarId)
        : undefined,
  };
};
